<template>
  <SidebarNavigation :uri="uri">
    <div class="resume-builder min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <h1 class="text-xl sm:text-2xl font-bold text-gray-900 truncate">Digital Resume / CV Generator</h1>
          <div v-if="currentResume" class="flex items-center gap-2">
            <span class="text-xs sm:text-sm text-gray-500 truncate">
              Editing: {{ currentResume.title }}
            </span>
            <span v-if="hasUnsavedChanges" class="text-xs text-orange-600 font-medium">• Unsaved changes</span>
            <span v-else-if="lastSaved" class="text-xs text-gray-400">• Saved {{ formatTime(lastSaved) }}</span>
            <span v-if="autoSaving" class="text-xs text-blue-600 font-medium">• Auto-saving...</span>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            v-if="currentResume"
            @click="saveResume"
            :disabled="saving"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {{ saving ? 'Saving...' : 'Save Resume' }}
          </button>
          <button
            v-if="currentResume"
            @click="togglePublic"
            :disabled="saving"
            :class="[
              'flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md',
              currentResume.is_public
                ? 'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800'
                : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
            ]"
          >
            {{ currentResume.is_public ? 'Make Private' : 'Make Public' }}
          </button>
          <button
            v-if="currentResume"
            @click="viewResume"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors shadow-sm hover:shadow-md"
          >
            View Resume
          </button>
          <button
            v-if="currentResume && currentResume.is_public"
            @click="copyResumeLink"
            class="flex-1 sm:flex-none px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm hover:shadow-md"
            title="Copy Resume Link for Link-in-Bio"
          >
            Copy Link
          </button>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row h-[calc(100vh-73px)] sm:h-[calc(100vh-81px)]">
        <!-- Loading Overlay -->
        <div v-if="loadingResume" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <svg class="animate-spin h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-700 font-medium">Loading resume...</p>
          </div>
        </div>
        
        <!-- Left Sidebar: Sections -->
        <div class="w-full lg:w-80 bg-white border-r border-gray-200 overflow-y-auto border-b lg:border-b-0">
          <div v-if="currentResume" class="p-4">
            <div class="mb-6 pb-6 border-b border-gray-200">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="plus" class="w-5 h-5 text-green-600" />
                Add Sections
              </h3>
              <div class="space-y-2">
                <button
                  v-for="sectionType in availableSectionTypes"
                  :key="sectionType.type"
                  @click="addSection(sectionType.type)"
                  class="w-full text-left px-4 py-3 bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-400 rounded-lg transition-all flex items-center gap-3 shadow-sm hover:shadow-md"
                >
                  <icon :name="sectionType.icon" class="w-5 h-5 text-gray-600" />
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ sectionType.name }}</div>
                    <div class="text-xs text-gray-500">{{ sectionType.description }}</div>
                  </div>
                </button>
              </div>
            </div>

            <div class="mb-6 pb-6 border-b border-gray-200">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="gear" class="w-5 h-5 text-green-600" />
                Design Settings
              </h3>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Theme Color</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-model="currentResume.theme_color"
                      type="color"
                      class="w-16 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-green-500 transition-colors"
                    />
                    <div class="flex-1">
                      <input
                        type="text"
                        :value="currentResume.theme_color"
                        @input="currentResume.theme_color = $event.target.value; validateThemeColor(); hasUnsavedChanges = true; autoSaveResume()"
                        @blur="validateThemeColor()"
                        :class="[
                          'w-full px-3 py-2 border-2 rounded-lg focus:ring-2 text-sm font-mono',
                          validationErrors.theme_color ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                        ]"
                        placeholder="#3b82f6"
                      />
                      <p v-if="validationErrors.theme_color" class="text-xs text-red-500 mt-1">{{ validationErrors.theme_color }}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    v-model="currentResume.font_family"
                    class="form-select w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Lato">Lato</option>
                    <option value="Montserrat">Montserrat</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Layout</label>
                    <select
                      v-model="currentResume.layout"
                      @change="hasUnsavedChanges = true; autoSaveResume()"
                      class="form-select w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                    <option value="one-column">One Column</option>
                    <option value="two-column">Two Column</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Sections List -->
            <div v-if="sortedSections.length > 0">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <icon name="category" class="w-5 h-5 text-green-600" />
                Sections ({{ sortedSections.length }})
              </h3>
              <div class="space-y-2">
                <div
                  v-for="(section, index) in sortedSections"
                  :key="section.id"
                  @click="editSection(section)"
                  :class="[
                    'p-3 rounded-lg border-2 cursor-pointer transition-all',
                    editingSection?.id === section.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  ]"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">{{ index + 1 }}</span>
                      <span class="font-medium text-gray-900">{{ getSectionName(section.type) }}</span>
                    </div>
                    <button
                      @click.stop="deleteSection(section.id)"
                      class="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Delete section"
                    >
                      <icon name="trash" class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Center: A4 Preview -->
        <div class="flex-1 overflow-y-auto bg-gray-100 p-4 sm:p-6 lg:p-8 flex items-start justify-center">
          <div v-if="!currentResume" class="flex items-center justify-center h-full w-full">
            <div class="text-center">
              <icon name="page" class="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 class="text-xl font-semibold text-gray-700 mb-2">No resume selected</h3>
              <p class="text-gray-500 mb-4">Create a new resume to get started</p>
              <button
                @click="showCreateModal = true"
                class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Create New Resume
              </button>
            </div>
          </div>

          <div v-else class="resume-preview-container">
            <!-- A4-sized preview -->
            <div
              class="resume-preview bg-white shadow-2xl"
              :style="{
                fontFamily: currentResume.font_family,
                color: '#1f2937'
              }"
            >
              <div
                v-for="section in sortedSections"
                :key="section.id"
                class="resume-section relative group"
                @click="editSection(section)"
              >
                <div class="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    @click.stop="moveSection(section, 'up')"
                    :disabled="sortedSections.indexOf(section) === 0"
                    class="p-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 disabled:opacity-50"
                    title="Move Up"
                  >
                    <icon name="up" class="w-4 h-4" />
                  </button>
                  <button
                    @click.stop="moveSection(section, 'down')"
                    :disabled="sortedSections.indexOf(section) === sortedSections.length - 1"
                    class="p-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 disabled:opacity-50"
                    title="Move Down"
                  >
                    <icon name="down" class="w-4 h-4" />
                  </button>
                  <button
                    @click.stop="deleteSection(section.id)"
                    class="p-1 bg-red-500 text-white rounded shadow-sm hover:bg-red-600"
                    title="Delete"
                  >
                    <icon name="trash" class="w-4 h-4" />
                  </button>
                </div>
                <component
                  :is="getSectionComponent(section.type)"
                  :section="section"
                  :theme-color="currentResume.theme_color"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Right Sidebar: Resume List & Section Editor -->
        <div class="w-80 bg-white border-l border-gray-200 overflow-y-auto">
          <div v-if="!editingSection" class="p-4">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-semibold text-gray-800">Your Resumes</h2>
              <button
                @click="showCreateModal = true"
                class="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                title="Create New Resume"
              >
                <icon name="plus" class="w-5 h-5" />
              </button>
            </div>
            <div class="space-y-2">
              <div
                v-for="resume in resumesList"
                :key="resume.id"
                @click="loadResume(resume.id)"
                :class="[
                  'p-3 rounded-lg border cursor-pointer transition-colors',
                  currentResume && currentResume.id === resume.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                ]"
              >
                <div class="font-medium text-gray-900">{{ resume.title }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ resume.created_at }}</div>
                <div class="flex items-center gap-2 mt-2">
                  <span
                    :class="[
                      'text-xs px-2 py-0.5 rounded',
                      resume.is_public
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    ]"
                  >
                    {{ resume.is_public ? 'Public' : 'Private' }}
                  </span>
                  <span class="text-xs text-gray-500">{{ resume.views }} views</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Editor -->
          <div v-else class="p-4">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-semibold text-gray-800">Edit Section</h2>
              <button
                @click="editingSection = null"
                class="text-gray-500 hover:text-gray-700"
              >
                <icon name="cross" class="w-5 h-5" />
              </button>
            </div>
            <component
              :is="getSectionEditorComponent(editingSection.type)"
              :section="editingSection"
              @update="updateSection"
            />
          </div>
        </div>
      </div>

      <!-- Create Resume Modal -->
      <div
        v-if="showCreateModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        @click.self="showCreateModal = false"
      >
        <div class="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 class="text-xl font-semibold mb-4">Create New Resume</h3>
          <input
            v-model="newResumeTitle"
            @keyup.enter="createResume"
            type="text"
            placeholder="Resume Title"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"
            autofocus
          />
          <div class="flex gap-3 justify-end">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              @click="createResume"
              :disabled="!newResumeTitle || creating"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ creating ? 'Creating...' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import Icon from '@/Shared/Icon.vue'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'
import ProfileSection from './Sections/ProfileSection.vue'
import ExperienceSection from './Sections/ExperienceSection.vue'
import EducationSection from './Sections/EducationSection.vue'
import SkillsSection from './Sections/SkillsSection.vue'
import ProjectsSection from './Sections/ProjectsSection.vue'
import ProfileSectionEditor from './Sections/ProfileSectionEditor.vue'
import ExperienceSectionEditor from './Sections/ExperienceSectionEditor.vue'
import { saveToStorage, loadFromStorage, removeFromStorage } from '../../utils/storage.js'
import EducationSectionEditor from './Sections/EducationSectionEditor.vue'
import SkillsSectionEditor from './Sections/SkillsSectionEditor.vue'
import ProjectsSectionEditor from './Sections/ProjectsSectionEditor.vue'
import { validateHexColor } from '../../utils/validation.js'

export default {
  layout: Layout,
  components: {
    Icon,
    SidebarNavigation,
    ProfileSection,
    ExperienceSection,
    EducationSection,
    SkillsSection,
    ProjectsSection,
    ProfileSectionEditor,
    ExperienceSectionEditor,
    EducationSectionEditor,
    SkillsSectionEditor,
    ProjectsSectionEditor,
  },
  props: {
    resumes: {
      type: Array,
      default: () => []
    },
    uri: {
      type: String,
      default: 'resume'
    },
    title: {
      type: String,
      default: 'Digital Resume / CV Generator'
    }
  },
  data() {
    return {
      resumesList: this.resumes || [],
      currentResume: null,
      sections: [],
      editingSection: null,
      validationErrors: {},
      saving: false,
      creating: false,
      loadingResume: false,
      autoSaveTimer: null,
      autoSaving: false,
      lastSaved: null,
      hasUnsavedChanges: false,
      showCreateModal: false,
      newResumeTitle: '',
      availableSectionTypes: [
        {
          type: 'profile',
          name: 'Profile',
          description: 'Name, title, contact info',
          icon: 'user'
        },
        {
          type: 'experience',
          name: 'Experience',
          description: 'Work experience entries',
          icon: 'briefcase'
        },
        {
          type: 'education',
          name: 'Education',
          description: 'Education history',
          icon: 'academic-cap'
        },
        {
          type: 'skills',
          name: 'Skills',
          description: 'Skills and competencies',
          icon: 'star'
        },
        {
          type: 'projects',
          name: 'Projects',
          description: 'Project portfolio',
          icon: 'folder'
        }
      ]
    }
  },
  computed: {
    sortedSections() {
      return [...this.sections].sort((a, b) => a.order - b.order)
    }
  },
  created() {
    if (this.resumesList.length > 0) {
      this.loadResume(this.resumesList[0].id)
    }
  },
  mounted() {
    // Add beforeunload listener for unsaved changes warning
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeUnmount() {
    // Clean up
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer)
    }
  },
  methods: {
    handleBeforeUnload(e) {
      if (this.hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    },
    formatTime(date) {
      if (!date) return ''
      const now = new Date()
      const diff = Math.floor((now - date) / 1000)
      
      if (diff < 60) return 'just now'
      if (diff < 3600) return `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) > 1 ? 's' : ''} ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) > 1 ? 's' : ''} ago`
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    },
    getSectionName(type) {
      const sectionType = this.availableSectionTypes.find(st => st.type === type)
      return sectionType ? sectionType.name : type.charAt(0).toUpperCase() + type.slice(1)
    },
      async loadResume(resumeId) {
        this.loadingResume = true
        try {
          // Try to load from localStorage first for faster initial render
          const cachedData = loadFromStorage(`resume_${resumeId}`)
          if (cachedData) {
            this.currentResume = cachedData.resume
            this.sections = cachedData.sections || []
            this.editingSection = null
          }
          
          const response = await this.axios.get(this.route('resumes.edit', resumeId))
          if (response.data && response.data.success) {
            this.currentResume = response.data.resume
            this.sections = response.data.sections || []
            this.editingSection = null
            
            // Cache the data
            saveToStorage(`resume_${resumeId}`, {
              resume: response.data.resume,
              sections: response.data.sections || []
            })
          }
        } catch (error) {
        console.error('Error loading resume:', error)
        let errorMessage = 'Failed to load resume'
        
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'Resume not found. It may have been deleted.'
          } else if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'You do not have permission to view this resume.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }
        
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.loadingResume = false
      }
    },
    async createResume() {
      if (!this.newResumeTitle || this.creating) return

      this.creating = true
      try {
        const response = await this.axios.post(this.route('resumes.store'), {
          title: this.newResumeTitle,
          theme_color: '#17803d',
          font_family: 'Inter',
          layout: 'one-column'
        })

        if (response.data && response.data.success) {
          this.currentResume = response.data.resume
          this.sections = []
          this.resumesList.push({
            id: response.data.resume.id,
            slug: response.data.resume.slug,
            title: response.data.resume.title,
            is_public: false,
            views: 0,
            created_at: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          })
          // Update cache
          saveToStorage('resumes_list', this.resumesList)
          this.showCreateModal = false
          this.newResumeTitle = ''
        }
      } catch (error) {
        console.error('Error creating resume:', error)
        let errorMessage = 'Failed to create resume'
        
        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'You do not have permission to perform this action. Please refresh the page.'
          } else if (error.response.status === 422) {
            errorMessage = error.response.data?.message || 'Validation failed. Please check your input.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }
        
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.creating = false
        this.loading = false
      }
    },
    async saveResume(showToast = true) {
      if (!this.currentResume || this.saving || this.autoSaving) return

      this.saving = true
      try {
        // Save sections order
        const sectionsOrder = this.sortedSections.map(s => s.id)
        
        const response = await this.axios.put(
          this.route('resumes.update', this.currentResume.id),
          {
            title: this.currentResume.title,
            sections_order: sectionsOrder,
            theme_color: this.currentResume.theme_color,
            font_family: this.currentResume.font_family,
            layout: this.currentResume.layout,
            is_public: this.currentResume.is_public
          }
        )

        if (response.data && response.data.success) {
          this.currentResume = response.data.resume
          
          // Update localStorage cache
          if (this.currentResume) {
            saveToStorage(`resume_${this.currentResume.id}`, {
              resume: this.currentResume,
              sections: this.sections
            })
          }
          
          this.hasUnsavedChanges = false
          this.lastSaved = new Date()
          
          if (showToast && window.$toast) {
            window.$toast.success('Resume saved successfully!')
          }
        }
      } catch (error) {
        console.error('Error saving resume:', error)
        let errorMessage = 'Failed to save resume'
        
        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'Session expired. Please refresh the page and try again.'
          } else if (error.response.status === 422) {
            errorMessage = error.response.data?.message || 'Validation failed. Please check your input.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Your changes may not have been saved. Please try again.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Your changes may not have been saved. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }
        
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.saving = false
      }
    },
    autoSaveResume() {
      if (!this.currentResume || this.saving || this.autoSaving) return
      
      // Clear existing timer
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
      }
      
      // Set new timer (debounce for 2 seconds)
      this.autoSaveTimer = setTimeout(async () => {
        if (!this.currentResume || this.saving) return
        
        this.autoSaving = true
        try {
          await this.saveResume(false) // Don't show toast for auto-save
        } catch (error) {
          console.error('Auto-save error:', error)
        } finally {
          this.autoSaving = false
        }
      }, 2000)
    },
    async togglePublic() {
      if (!this.currentResume) return
      this.currentResume.is_public = !this.currentResume.is_public
      await this.saveResume()
    },
    addSection(type) {
      if (!this.currentResume) {
        if (window.$toast) {
          window.$toast.warning('Please create or select a resume first')
        }
        return
      }

      const defaultData = this.getDefaultSectionData(type)
      const newOrder = this.sections.length > 0 ? Math.max(...this.sections.map(s => s.order)) + 1 : 0

      const section = {
        id: null,
        type: type,
        data: defaultData,
        order: newOrder
      }

      this.sections.push(section)
      this.editingSection = { ...section }
      this.saveSection(section)
      this.hasUnsavedChanges = true
      this.autoSaveResume()
    },
    getDefaultSectionData(type) {
      switch (type) {
        case 'profile':
          return {
            fullName: '',
            title: '',
            email: '',
            phone: '',
            location: '',
            summary: ''
          }
        case 'experience':
          return {
            items: [{
              title: '',
              company: '',
              location: '',
              startDate: '',
              endDate: '',
              description: ['']
            }]
          }
        case 'education':
          return {
            items: [{
              degree: '',
              school: '',
              location: '',
              startDate: '',
              endDate: '',
              description: ''
            }]
          }
        case 'skills':
          return {
            items: []
          }
        case 'projects':
          return {
            items: [{
              name: '',
              description: '',
              technologies: '',
              url: ''
            }]
          }
        default:
          return {}
      }
    },
    editSection(section) {
      this.editingSection = JSON.parse(JSON.stringify(section))
    },
    async updateSection(updatedSection) {
      const index = this.sections.findIndex(s => s.id === updatedSection.id)
      if (index !== -1) {
        this.sections[index] = updatedSection
        await this.saveSection(updatedSection)
        this.hasUnsavedChanges = true
        this.autoSaveResume()
      }
    },
    async saveSection(section) {
      if (!this.currentResume) return

      try {
        const response = await this.axios.post(
          this.route('resumes.sections.store', this.currentResume.id),
          {
            id: section.id,
            type: section.type,
            data: section.data,
            order: section.order
          }
        )

        if (response.data && response.data.success) {
          const index = this.sections.findIndex(s => s.id === section.id || (!s.id && this.sections.indexOf(s) === this.sections.indexOf(section)))
          if (index !== -1) {
            this.sections[index] = response.data.section
          }
        }
      } catch (error) {
        console.error('Error saving section:', error)
      }
    },
    async deleteSection(sectionId) {
      if (!confirm('Delete this section?')) return

      try {
        await this.axios.delete(this.route('resumes.sections.destroy', sectionId))
        this.sections = this.sections.filter(s => s.id !== sectionId)
        if (this.editingSection && this.editingSection.id === sectionId) {
          this.editingSection = null
        }
        this.hasUnsavedChanges = true
        this.autoSaveResume()
      } catch (error) {
        console.error('Error deleting section:', error)
        let errorMessage = 'Failed to delete section'
        
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'Section not found. It may have already been deleted.'
          } else if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'You do not have permission to delete this section.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          }
        }
        
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      }
    },
    moveSection(section, direction) {
      const index = this.sortedSections.indexOf(section)
      const newIndex = direction === 'up' ? index - 1 : index + 1

      if (newIndex < 0 || newIndex >= this.sortedSections.length) return

      const temp = this.sortedSections[index].order
      this.sortedSections[index].order = this.sortedSections[newIndex].order
      this.sortedSections[newIndex].order = temp

      // Save both sections
      this.saveSection(this.sortedSections[index])
      this.saveSection(this.sortedSections[newIndex])
      this.hasUnsavedChanges = true
      this.autoSaveResume()
    },
    getSectionComponent(type) {
      switch (type) {
        case 'profile':
          return 'ProfileSection'
        case 'experience':
          return 'ExperienceSection'
        case 'education':
          return 'EducationSection'
        case 'skills':
          return 'SkillsSection'
        case 'projects':
          return 'ProjectsSection'
        default:
          return 'div'
      }
    },
    getSectionEditorComponent(type) {
      switch (type) {
        case 'profile':
          return 'ProfileSectionEditor'
        case 'experience':
          return 'ExperienceSectionEditor'
        case 'education':
          return 'EducationSectionEditor'
        case 'skills':
          return 'SkillsSectionEditor'
        case 'projects':
          return 'ProjectsSectionEditor'
        default:
          return 'div'
      }
    },
    viewResume() {
      if (!this.currentResume) return
      window.open(this.route('resume.view', this.currentResume.slug), '_blank')
    },
    async copyResumeLink() {
      if (!this.currentResume || !this.currentResume.is_public) {
        if (window.$toast) {
          window.$toast.warning('Please make the resume public first')
        }
        return
      }
      
      const url = window.location.origin + this.route('resume.view', this.currentResume.slug)
      
      try {
        await navigator.clipboard.writeText(url)
        if (window.$toast) {
          window.$toast.success('Resume link copied! You can now add it to your Link-in-Bio page.')
        }
      } catch (error) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        if (window.$toast) {
          window.$toast.success('Resume link copied! You can now add it to your Link-in-Bio page.')
        }
      }
    }
  }
}
</script>

<style scoped>
.resume-builder {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.resume-preview-container {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1rem;
}

@media (min-width: 640px) {
  .resume-preview-container {
    padding: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .resume-preview-container {
    padding: 2rem;
  }
}

.resume-preview {
  width: 100%;
  max-width: 210mm; /* A4 width */
  min-height: 297mm; /* A4 height */
  padding: 15mm;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .resume-preview {
    padding: 18mm;
  }
}

@media (min-width: 1024px) {
  .resume-preview {
    padding: 20mm;
  }
}

.resume-section {
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  border: 2px dashed transparent;
}

.resume-section:hover {
  border-color: #3b82f6;
}

@media print {
  .resume-preview {
    box-shadow: none;
    margin: 0;
    padding: 20mm;
  }
}
</style>

